/**
 * Non-React intl entry points.
 *
 * ⚠️ This module deliberately keeps *no* `IntlShape` singleton that gets
 * mutated while React renders. `t()` resolves against an explicitly installed
 * active locale and exists for non-React call sites only (scripts, previews,
 * test helpers, event handlers outside the tree). Anything that renders through
 * React must use `IntlProvider` / `useIntl` / `FormattedMessage`, which carry
 * the `IntlShape` in React context and are therefore safe under concurrent SSR.
 */

import { createIntl, createIntlCache } from "react-intl";
import type { IntlShape } from "react-intl";
import type { PrimitiveType } from "react-intl";
import type { FormatXMLElementFn } from "intl-messageformat";

import en from "./lang/en.json";
import { DEFAULT_LOCALE, normalizeLocale, type TLocale } from "./locales";

/** Canonical English catalog. Always the base of every resolved catalog. */
const enMessages = en as TVocabulary;

/** Flat `id -> message` catalog, as consumed by react-intl. */
export type TVocabulary = Record<string, string>;

/** Values that can be interpolated into a message. */
export type TMessageValues = Record<
  string,
  PrimitiveType | FormatXMLElementFn<string, string>
>;

/**
 * Message ids declared in code, i.e. the keys extracted into
 * `lang/_source.json`.
 *
 * Until the first extraction pass produces messages this is `never`, which
 * would make `t()` unusable. {@link TMessageKey} therefore widens to `string`
 * while the catalog is empty and tightens to the exact id union afterwards, so
 * typos become compile errors as soon as there is something to be exhaustive
 * about.
 */
export type MessageId = keyof typeof en;

export type TMessageKey = [MessageId] extends [never] ? string : MessageId;

const cache = createIntlCache();

/** Catalogs loaded and installed in this process, keyed by canonical locale. */
const catalogs = new Map<TLocale, TVocabulary>();

/** Memoized `IntlShape` per locale. Keyed by locale, never mutated in place. */
const intlByLocale = new Map<TLocale, IntlShape>();

/**
 * One loader per locale.
 *
 * Kept as static `import()` calls so bundlers can code-split every catalog and
 * only ship the locale(s) a request actually needs.
 */
const localeLoaders: Record<TLocale, () => Promise<{ default: TVocabulary }>> =
  {
    en: () => import("./lang/en.json"),
    es: () => import("./lang/es.json"),
    pt: () => import("./lang/pt.json"),
    fr: () => import("./lang/fr.json"),
    de: () => import("./lang/de.json"),
  };

/** Locale used by {@link t}. Set through {@link setupLocale} / {@link setVocabulary}. */
let activeLocale: TLocale = DEFAULT_LOCALE;

function warn(message: string, error?: unknown): void {
  if (process.env.NODE_ENV === "production") return;
  // eslint-disable-next-line no-console
  console.warn(`[@atlas/text] ${message}`, error ?? "");
}

/**
 * Never throw from react-intl: a missing translation resolves to the English
 * base, and a formatting problem degrades to the default message. Anything that
 * cannot be formatted is reported once in development.
 */
export function handleIntlError(error: { message: string }): void {
  warn(error.message);
}

/**
 * Resolve a catalog for `locale`.
 *
 * The English catalog is always merged underneath, so a key that is missing
 * from a translation renders its canonical English message rather than the raw
 * id. The English base is the bundled `lang/en.json` plus anything installed
 * through {@link setVocabulary} / {@link setupLocale} for `en`.
 *
 * Explicit `overrides` (a per-request catalog) win over both.
 */
export function getMessages(
  locale?: TLocale | string | null,
  overrides?: TVocabulary
): TVocabulary {
  const resolved = normalizeLocale(locale);

  return {
    ...enMessages,
    ...(catalogs.get(DEFAULT_LOCALE) ?? {}),
    ...(catalogs.get(resolved) ?? {}),
    ...(overrides ?? {}),
  };
}

/**
 * Build an `IntlShape` for `locale`.
 *
 * The shape is memoized per locale and never mutated, so it is safe to share
 * across trees.
 */
export function createIntlForLocale(
  locale?: TLocale | string | null,
  overrides?: TVocabulary
): IntlShape {
  const resolved = normalizeLocale(locale);

  if (!overrides) {
    const cached = intlByLocale.get(resolved);
    if (cached) return cached;

    const intl = createIntl(
      {
        locale: resolved,
        defaultLocale: DEFAULT_LOCALE,
        messages: getMessages(resolved),
        onError: handleIntlError,
      },
      cache
    );

    intlByLocale.set(resolved, intl);
    return intl;
  }

  return createIntl(
    {
      locale: resolved,
      defaultLocale: DEFAULT_LOCALE,
      messages: getMessages(resolved, overrides),
      onError: handleIntlError,
    },
    cache
  );
}

/**
 * Install a catalog for `locale` and drop memoized `IntlShape`s.
 *
 * The whole memo is dropped because English sits underneath every locale: a
 * late `en` install changes what "missing key" resolves to everywhere.
 */
function installCatalog(locale: TLocale, vocabulary: TVocabulary): void {
  catalogs.set(locale, vocabulary);
  intlByLocale.clear();
}

/**
 * Load the catalog for `locale` (code-split) and install it.
 *
 * Resolves to the canonical locale that was actually installed. Unknown or
 * malformed locales fall back to English instead of throwing.
 */
export async function loadCatalog(
  locale: TLocale | string | null | undefined
): Promise<TLocale> {
  const resolved = normalizeLocale(locale);

  // Warn only when the requested *language* could not be honored. A legacy
  // spelling such as `en_US` or `de-DE` normalizes cleanly and is not a problem.
  if (typeof locale === "string") {
    const language = locale.trim().toLowerCase().split(/[-_]/)[0];
    if (language !== resolved) {
      warn(`Unsupported locale ${JSON.stringify(locale)}, using "${resolved}"`);
    }
  }

  if (catalogs.has(resolved)) return resolved;

  try {
    const loaded = await localeLoaders[resolved]();
    installCatalog(
      resolved,
      loaded.default ?? (loaded as unknown as TVocabulary)
    );
  } catch (error) {
    warn(
      `Could not load the "${resolved}" catalog, falling back to "${DEFAULT_LOCALE}"`,
      error
    );
    return DEFAULT_LOCALE;
  }

  return resolved;
}

/**
 * Load `locale` and make it the active locale for {@link t}.
 *
 * Preserves the code-splitting of the previous implementation while removing
 * the hard `throw` on unknown locales.
 */
export async function setupLocale(
  locale: TLocale | string | null | undefined
): Promise<TLocale> {
  const resolved = await loadCatalog(locale);
  activeLocale = resolved;

  return resolved;
}

/**
 * Synchronously install a catalog.
 *
 * Kept for call sites that statically import a catalog (tests, previews) and
 * cannot await. Prefer {@link setupLocale} in application code.
 */
export function setVocabulary(
  locale: TLocale | string | null | undefined,
  vocabulary: TVocabulary
): TLocale {
  const resolved = normalizeLocale(locale);

  installCatalog(resolved, vocabulary);
  activeLocale = resolved;

  return resolved;
}

/** The locale {@link t} currently resolves against. */
export function getActiveLocale(): TLocale {
  return activeLocale;
}

/** `IntlShape` for `locale`, or for the active locale when omitted. */
export function getIntl(
  locale?: TLocale | string | null,
  overrides?: TVocabulary
): IntlShape {
  return createIntlForLocale(locale ?? activeLocale, overrides);
}

/**
 * Translate a message outside of React.
 *
 * Falls back to the English message (and then to the id) instead of throwing.
 * Not safe to call while a React tree is rendering, use `useIntl()` there.
 */
export function t(id: TMessageKey, values?: TMessageValues): string {
  return getIntl().formatMessage({ id }, values);
}

/**
 * Create a locale-scoped `t`, bound to an explicit locale.
 *
 * This is the SSR-friendly alternative to the module-level active locale: pass
 * the request locale and the returned function cannot bleed into another
 * request.
 */
export function createTranslator(
  locale?: TLocale | string | null,
  overrides?: TVocabulary
): (id: TMessageKey, values?: TMessageValues) => string {
  const intl = createIntlForLocale(locale, overrides);

  return (id: TMessageKey, values?: TMessageValues) =>
    intl.formatMessage({ id }, values);
}

/** Reset installed catalogs and the active locale. Intended for tests. */
export function resetText(): void {
  catalogs.clear();
  intlByLocale.clear();
  activeLocale = DEFAULT_LOCALE;
}

export { createIntl, createIntlCache };
export type { IntlShape };
